import { lazy, type ComponentType } from "react"
import { Spin } from "antd"
import { PAGE_PATH_BY_KEY, PAGE_SECONDARY_CHUNK_PATHS } from "./pageAccess"

export { PAGE_PATH_BY_KEY, PAGE_SECONDARY_CHUNK_PATHS }

type PageModule = { default: ComponentType }

const lazyPage = (loader: () => Promise<PageModule>) => lazy(loader)

export const LazyAssistant = lazyPage(() => import("./components/Assistant"))
export const LazyOrdersList = lazyPage(() => import("./components/OrdersList"))
export const LazyProductsList = lazyPage(() => import("./components/ProductsList"))
export const LazyAmzProducts = lazyPage(() => import("./components/AmzProducts"))
export const LazyAmzProductsHub = lazy(() =>
  import("./components/AmzProducts").then(m => ({ default: m.AmzProductsHub })),
)
export const LazyAmzProductHistory = lazy(() =>
  import("./components/AmzProducts").then(m => ({ default: m.AmzProductHistoryPage })),
)
export const LazyAmzProductFees = lazy(() =>
  import("./components/AmzProducts").then(m => ({ default: m.AmzProductFeesPage })),
)
export const LazyProductPlanning = lazyPage(() => import("./components/ProductPlanning"))
export const LazyDashboard = lazyPage(() => import("./components/Dashboard"))
export const LazyOptimization = lazyPage(() => import("./components/Optimization"))
export const LazyModes = lazyPage(() => import("./components/Modes"))
export const LazyEvents = lazyPage(() => import("./components/Events"))
export const LazyKeywords = lazyPage(() => import("./components/KeywordModes"))
export const LazyTags = lazyPage(() => import("./components/Tags"))
export const LazyPortfolios = lazyPage(() => import("./components/Portfolios"))
export const LazyMarketplaces = lazyPage(() => import("./components/Marketplaces"))
export const LazyRuns = lazyPage(() => import("./components/Runs"))
export const LazyRunDetail = lazyPage(() => import("./components/RunDetail"))
export const LazyDayparting = lazyPage(() => import("./components/Dayparting"))
export const LazyListingAudit = lazyPage(() => import("./components/ListingAudit"))
export const LazyListingAuditDetail = lazyPage(() => import("./components/ListingAuditDetail"))
export const LazyAccountHealth = lazyPage(() => import("./components/AccountHealth"))
export const LazyNegatives = lazyPage(() => import("./components/Negatives"))
export const LazyHarvestingRules = lazyPage(() => import("./components/HarvestingRules"))
export const LazyHarvestingStudio = lazyPage(() => import("./components/HarvestingStudio"))
export const LazyHarvestingCandidates = lazyPage(() => import("./components/HarvestingCandidates"))
export const LazyRepricerRules = lazyPage(() => import("./components/RepricerRules"))
export const LazyRepricerStudio = lazyPage(() => import("./components/RepricerStudio"))
export const LazyRepricerCandidates = lazyPage(() => import("./components/RepricerCandidates"))
export const LazySettings = lazyPage(() => import("./components/Settings"))
export const LazyMonitoring = lazyPage(() => import("./components/Monitoring"))
export const LazyChangeHistory = lazyPage(() => import("./components/ChangeHistory"))
export const LazyUsers = lazyPage(() => import("./components/Users"))
export const LazyForbidden = lazyPage(() => import("./components/Forbidden"))
export const LazyNotFound = lazyPage(() => import("./components/NotFound"))

/** Dynamic import loaders keyed by primary nav path (webpack dedupes shared chunks). */
export const PAGE_CHUNK_LOADERS: Record<string, () => Promise<unknown>> = {
  [PAGE_PATH_BY_KEY.assistant]: () => import("./components/Assistant"),
  [PAGE_PATH_BY_KEY.orders]: () => import("./components/OrdersList"),
  [PAGE_PATH_BY_KEY.products]: () => import("./components/ProductsList"),
  [PAGE_PATH_BY_KEY["amz-products"]]: () => import("./components/AmzProducts"),
  [PAGE_SECONDARY_CHUNK_PATHS[0]]: () => import("./components/AmzProducts"),
  [PAGE_SECONDARY_CHUNK_PATHS[1]]: () => import("./components/AmzProducts"),
  [PAGE_PATH_BY_KEY["product-planning"]]: () => import("./components/ProductPlanning"),
  [PAGE_PATH_BY_KEY.dashboard]: () => import("./components/Dashboard"),
  [PAGE_PATH_BY_KEY.modes]: () => import("./components/Modes"),
  [PAGE_PATH_BY_KEY.events]: () => import("./components/Events"),
  [PAGE_PATH_BY_KEY.keywords]: () => import("./components/KeywordModes"),
  [PAGE_PATH_BY_KEY.tags]: () => import("./components/Tags"),
  [PAGE_PATH_BY_KEY.portfolios]: () => import("./components/Portfolios"),
  [PAGE_PATH_BY_KEY.marketplaces]: () => import("./components/Marketplaces"),
  [PAGE_PATH_BY_KEY.runs]: () => import("./components/Optimization"),
  [PAGE_PATH_BY_KEY.dayparting]: () => import("./components/Dayparting"),
  [PAGE_PATH_BY_KEY["listing-audit"]]: () => import("./components/ListingAudit"),
  [PAGE_PATH_BY_KEY["account-health"]]: () => import("./components/AccountHealth"),
  [PAGE_PATH_BY_KEY.negatives]: () => import("./components/Negatives"),
  [PAGE_PATH_BY_KEY.harvesting]: () => import("./components/HarvestingRules"),
  [PAGE_PATH_BY_KEY.repricer]: () => import("./components/RepricerRules"),
  [PAGE_PATH_BY_KEY.settings]: () => import("./components/Settings"),
  "/monitoring": () => import("./components/Monitoring"),
  [PAGE_PATH_BY_KEY.history]: () => import("./components/ChangeHistory"),
  [PAGE_PATH_BY_KEY.users]: () => import("./components/Users"),
}

/** Warm the route chunk before navigation (no-op for unknown paths). */
export function prefetchPageChunk(path: string): void {
  const loader = PAGE_CHUNK_LOADERS[path]
  if (loader) void loader()
  if (path === PAGE_PATH_BY_KEY.runs) {
    void import("./components/Runs")
    void import("./components/RunDetail")
  }
}

export const PageRouteFallback = () => (
  <div className="page-route-loading" aria-busy="true" aria-label="Loading page">
    <Spin />
  </div>
)
